import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import clsx from 'clsx';

/**
 * @typedef {Object} ModalProps
 * @property {boolean} isOpen - Whether modal is open
 * @property {Function} onClose - Function to close modal
 * @property {string} [title] - Modal title
 * @property {React.ReactNode} children - Modal content
 * @property {'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'} [size] - Modal size
 * @property {boolean} [showCloseButton] - Whether to show close button
 * @property {string} [className] - Additional CSS classes
 */

/**
 * Reusable Modal component with backdrop and animations
 * @param {ModalProps} props
 * @returns {JSX.Element|null}
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  className = ''
}) => {
  const dialogRef = useRef(null);
  const lastActiveRef = useRef(null);
  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusableSelectors = [
          'a[href]',
          'button:not([disabled])',
          'textarea',
          'input',
          'select',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',');
        const focusables = Array.from(root.querySelectorAll(focusableSelectors));
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        const isInside = root.contains(active);
        if (e.shiftKey) {
          if (!isInside || active === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (!isInside || active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    if (isOpen) {
      // Store previously focused element to restore after close
      lastActiveRef.current = document.activeElement;
      document.addEventListener('keydown', handleKeyDown);
      // Do not modify document.body.style.overflow; keep scrollbar visible per project preference
      // Move focus to first focusable element inside modal
      setTimeout(() => {
        const root = dialogRef.current;
        if (!root) return;
        const focusableSelectors = [
          'a[href]',
          'button:not([disabled])',
          'textarea',
          'input',
          'select',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',');
        const focusables = Array.from(root.querySelectorAll(focusableSelectors));
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          root.focus?.();
        }
      }, 0);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the previously active element
      if (lastActiveRef.current && typeof lastActiveRef.current.focus === 'function') {
        try { lastActiveRef.current.focus(); } catch (_) { /* noop */ }
      }
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[90] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
        
        {/* Modal */}
        <div
          ref={dialogRef}
          className={clsx(
            'relative bg-white rounded-lg shadow-xl w-full',
            sizes[size],
            className
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          tabIndex={-1}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              {title && (
                <h2 
                  id="modal-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  {title}
                </h2>
              )}
              
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          )}
          
          {/* Content */}
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}; 